import React, { useCallback } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import productUtils from 'utils/product';
import { Flex, Image } from 'components/ui';
import { IMAGE_SIZES, SHADOW, THUMB_COUNT } from 'components/ProductPage/ProductMediaCarousel/constants';
import analyticsConstants from 'analytics/constants';
import { breakpoints, radii } from 'style/config';
import { THUMBNAIL_PRODUCT_MEDIA_ITEM } from 'style/imageSizes';
import BccVideo from 'components/Bcc/BccVideo/BccVideo';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import skuUtils from 'utils/Sku';
import imageUtils from 'utils/Image';

const { MEDIA_TYPE } = analyticsConstants;
const { buildProductHeroImageSrc } = productUtils;
const { getImageSrc, getImageBadgeFromUrl } = imageUtils;

const SvgImage = React.memo(
    ({
        imgSize, enableZoom, item, isLazy, imageSource, imageSourceX2, onLoadPerfMark, elementTimingIdentifier, includePerfMark
    }) => {
        const elementTimingAttr = elementTimingIdentifier ? 'elementtiming="' + elementTimingIdentifier + '" ' : '';

        const svgRef = useCallback(
            node => {
                if (!node) {
                    return;
                }

                if (includePerfMark) {
                    Sephora.Util.Perf.imageExpectedDedup(imageSource);
                }

                if (elementTimingIdentifier && Sephora._earlyElementTiming) {
                    Sephora._earlyElementTiming.expectElement(imageSource);
                }
            },
            [imageSource, includePerfMark, elementTimingIdentifier]
        );

        return (
            // <img> needs to be set with dangerouslySetInnerHTML so that image onload event
            // will be triggered for product page render tracking
            // https://jira.sephora.com/browse/UA-604
            <svg
                ref={svgRef}
                width={imgSize}
                height={imgSize}
                css={styles.svgWrapper}
                dangerouslySetInnerHTML={{
                    __html:
                        '<foreignObject x="0" y="0" width="' +
                        imgSize +
                        '" height="' +
                        imgSize +
                        '" style="' +
                        (enableZoom ? 'mask: url(#heroHoverMediaMask)' : '') +
                        '"><img alt="' +
                        item.media.altText +
                        '" ' +
                        elementTimingAttr +
                        'width="' +
                        imgSize +
                        '" height="' +
                        imgSize +
                        '" src="' +
                        imageSource +
                        '" srcset="' +
                        imageSource +
                        ' 1x, ' +
                        imageSourceX2 +
                        ' 2x" ' +
                        ' style="object-fit: contain" ' +
                        (isLazy ? ' loading="lazy" ' : '') +
                        onLoadPerfMark +
                        '/></foreignObject>'
                }}
            ></svg>
        );
    }
);

const renderThumbnailImage = ({ thumbSrc, isLazy, altText }) => (
    <picture css={styles.picture}>
        <source
            media={breakpoints.mdMin}
            srcSet={getImageSrc(thumbSrc, THUMBNAIL_PRODUCT_MEDIA_ITEM, true)}
        />
        <Image
            src='/img/ufe/placeholder.png'
            size={THUMBNAIL_PRODUCT_MEDIA_ITEM}
            disableLazyLoad={!isLazy}
            alt={altText}
            borderRadius='full'
            overflow='hidden'
            css={styles.img}
        />
    </picture>
);

const renderFullWidthImage = ({
    src,
    item,
    isLazy,
    onClick,
    index,
    isSingleItem,
    includePerfMark,
    isHeroImage,
    sealBadge,
    showPDPTopOfPageRedesign
}) => (
    <ProductImage
        src={src}
        hideBadge={!sealBadge}
        badge={sealBadge || undefined}
        size={showPDPTopOfPageRedesign ? '100%' : IMAGE_SIZES.SMALL}
        onClick={onClick(index)}
        disableLazyLoad={!isLazy}
        altText={item.altText}
        borderRadius={showPDPTopOfPageRedesign ? 0 : 2}
        overflow='hidden'
        isPageRenderImg={includePerfMark}
        elementTimingId={isHeroImage ? src : undefined}
        boxShadow={!isSingleItem && !showPDPTopOfPageRedesign && SHADOW}
    />
);

const renderImageMediaItem = ({
    item,
    index,
    isFullWidth,
    onClick,
    isThumbnail,
    preventPageRenderReport,
    maskSize,
    isSingleItem,
    enableZoom,
    showPDPTopOfPageRedesign,
    isHeroImage
}) => {
    const src = skuUtils.getImgSrc(IMAGE_SIZES.ZOOM, item.media);

    const isLazy = isThumbnail ? index >= THUMB_COUNT : isFullWidth ? index > 1 : index > 0;
    const includePerfMark = index === 0 && !preventPageRenderReport;
    const showSealOnFirstImage = !!showPDPTopOfPageRedesign && index === 0;
    const queryStart = src.indexOf('?');
    const sealBadge = showSealOnFirstImage && queryStart >= 0 ? getImageBadgeFromUrl(src.slice(queryStart + 1)) : null;

    if (isThumbnail) {
        return renderThumbnailImage({
            thumbSrc: src,
            isLazy,
            altText: item.altText
        });
    }

    if (isFullWidth) {
        return renderFullWidthImage({
            src,
            item,
            isLazy,
            onClick,
            index,
            isSingleItem,
            includePerfMark,
            isHeroImage,
            sealBadge,
            showPDPTopOfPageRedesign
        });
    }

    return (
        <button onClick={onClick(index)}>
            {getSvgForLargeView({
                item,
                imgSize: maskSize,
                enableZoom,
                includePerfMark,
                isLazy,
                isHeroImage,
                keepBadge: !!sealBadge
            })}
        </button>
    );
};

const renderVideoMediaItem = ({
    item, index, isFullWidth, onClick, isThumbnail, maskSize, skuId, showPDPTopOfPageRedesign
}) => {
    const isLazy = isThumbnail ? index >= THUMB_COUNT : isFullWidth ? index > 1 : index > 0;
    const fullWidthThumbnailWidth = showPDPTopOfPageRedesign ? '100%' : IMAGE_SIZES.SMALL;

    return (
        <Flex
            flexDirection='column'
            justifyContent='center'
            borderRadius={isThumbnail ? 'full' : showPDPTopOfPageRedesign ? 0 : 2}
            overflow='hidden'
            onClick={!isThumbnail ? onClick(index) : null}
            boxShadow={isThumbnail || (!showPDPTopOfPageRedesign && SHADOW)}
        >
            <BccVideo
                isContained={false}
                videoId={item.media.filePath}
                name={item.media.name}
                startImagePath={item.media.startImagePath}
                thumbnailWidth={isThumbnail ? THUMBNAIL_PRODUCT_MEDIA_ITEM : isFullWidth ? fullWidthThumbnailWidth : maskSize}
                thumbnailRatio={isThumbnail ? 1 : 9 / 16}
                overlayFlag={true}
                isButton={false}
                isSmallThumb={isThumbnail}
                disableLazyLoad={!isLazy}
                hideDescription={true}
                skuId={skuId}
            />
        </Flex>
    );
};

function ProductMediaItem(props) {
    const { item, index, isThumbnail } = props;

    // Element Timing must NOT be gated by preventPageRenderReport because on product pages,
    // React's batched re-render (from ProductMediaCarousel.componentDidMount setState + Redux dispatch
    // in RegularProduct.componentDidMount) causes preventPageRenderReport to be true by the time
    // the carousel's first real render replaces the placeholder. Load-event tracking (includePerfMark)
    // still uses preventPageRenderReport since it has its own dedup/counter mechanism.
    const isHeroImage = index === 0 && !isThumbnail && item.type === MEDIA_TYPE.IMAGE;

    switch (item.type) {
        case MEDIA_TYPE.IMAGE:
            return renderImageMediaItem({ ...props, isHeroImage });
        case MEDIA_TYPE.VIDEO:
            return renderVideoMediaItem(props);
        default:
            return <div></div>;
    }
}

const getSvgForLargeView = ({
    item, imgSize, enableZoom, includePerfMark, isLazy, isHeroImage, keepBadge
}) => {
    const [imageSource, imageSourceX2] = buildProductHeroImageSrc(item, false, keepBadge);
    const onLoadPerfMark = includePerfMark
        ? 'onload=\'' +
          // "console.log('Onload Image Load Triggered'); " +
          'Sephora.Util.Perf.markPageRenderDedup("' +
          imageSource +
          '", arguments[0].target.currentSrc || arguments[0].target.src);\'}'
        : '';

    return (
        <SvgImage
            imgSize={imgSize}
            enableZoom={enableZoom}
            item={item}
            isLazy={isLazy}
            imageSource={imageSource}
            imageSourceX2={imageSourceX2}
            onLoadPerfMark={onLoadPerfMark}
            includePerfMark={includePerfMark}
            elementTimingIdentifier={isHeroImage ? imageSource : undefined}
        />
    );
};

const styles = {
    picture: {
        display: 'block',
        width: '100%',
        height: '100%'
    },
    svgWrapper: {
        display: 'block',
        pointerEvents: 'none',
        borderRadius: radii[2]
    },
    img: {
        objectFit: 'cover'
    }
};

export default wrapFunctionalComponent(ProductMediaItem, 'ProductMediaItem');
